import './patientDetailsScreen.scss'
import React, {useCallback, useEffect, useState} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {CommonService} from "../../../shared/services";
import {IAPIResponseType} from "../../../shared/models/api.model";
import StatusCardComponent from "../../../shared/components/status-card/statusCardComponent";
import HeadingComponent from "../../../shared/components/heading/headingComponent";
import TabsWrapperComponent, {
    TabComponent,
    TabContentComponent,
    TabsComponent
} from "../../../shared/components/tabs/tabsComponent";
import PatientOrderHistoryComponent from "./patient-order-history/patientOrderHistoryComponent";
import PatientInformationDetailsComponent from "./patient-information-details/patientInformationDetailsComponent";
import PatientInsuranceDetailsComponent from "./patient-insurance-details/patientInsuranceDetailsComponent";
import DetailsDataKeyValueComponent
    from "../../../shared/components/details-data-key-value-component/detailsDataKeyValueComponent";
import {useDispatch, useSelector} from "react-redux";
import {setCurrentNavParams, setSideMenuView} from "../../../store/actions/header.action";
import {ImageConfig} from "../../../constants";
import LottieFileGenarationComponent
    from "../../../shared/components/core/lottie-file-genaration/LottieFileGenarationComponent";
import PatientActivityLogsComponent from "./patient-activity-logs/PatientActivityLogsComponent";
import commonService from "../../../shared/services/common.service";
import {IRootReducerState} from "../../../store/reducers";
import ButtonComponent from "../../../shared/components/core/button/ButtonComponent";
import PatientAdditionalDetailsComponent from "./patient-additional-details/PatientAdditionalDetailsComponent";

const PATIENT_INFORMATION_TAB = 'patient_information';
const INSURANCE_DETAILS_TAB = 'insurance_details';
const ORDER_HISTORY_TAB = 'order_history';
const PATIENT_ACTIVITY_LOGS = 'patient_activity_logs';
const ADDITIONAL_DETAILS_TAB = 'additional_details';

interface PatientDetailsScreenProps {
}

const PatientDetailsScreen = (props: PatientDetailsScreenProps) => {
        const [activeTab, setActiveTab] = useState<"patient_information" | "insurance_details" | "order_history" | "patient_activity_logs" | "additional_details">(PATIENT_INFORMATION_TAB);
        const navigate = useNavigate();
        const params = useParams<{ patientID: string | undefined }>();
        const {patientID} = params;
        const [patientDetails, setPatientDetails] = useState<any>(undefined);
        const [isPatientDetailsLoading, setIsPatientDetailsLoading] = useState<boolean>(false);
        const [isPatientDetailsLoaded, setIsPatientDetailsLoaded] = useState<boolean>(false);
        const [isPatientDetailsLoadingFailed, setIsPatientDetailsLoadingFailed] = useState<boolean>(false);
        const [patientDetailsMode, setPatientDetailsMode] = useState<"view" | "edit">("view");
        const {currentUser} = useSelector((state: IRootReducerState) => state.account);
        const [canEdit, setCanEdit] = useState<boolean>(false);
        const dispatch = useDispatch();

        useEffect(() => {
            setCanEdit(commonService.checkAccess('Patient',
                'patientEdit', currentUser?.userAccessModules))
        }, [currentUser]);

        useEffect(() => {
            if (patientID) {
                dispatch(setCurrentNavParams("Back", "Patient", CommonService._routeConfig.PatientListRoute()));
                dispatch(setSideMenuView('compact'));
            }
        }, [patientID, dispatch, navigate]);

        const handleActiveTabChange = useCallback((value: any) => {
            if (patientDetailsMode === 'edit') {
                CommonService.openConfirmationDialog(
                    {
                        confirmationTitle: 'Please confirm!',
                        confirmationSubTitle: 'Are you sure you want to discard the recent changes?',
                        disableEscKeyClose: false,
                        disableBackdropClose: true,
                        imagePath: `${ImageConfig.ConfirmationImage}`,
                        direction: "up",
                        yes: {
                            text: "Discard",
                            color: "primary"
                        },
                        no: {
                            text: "Cancel",
                            color: "primary"
                        }
                    }).then(() => {
                    setActiveTab(value);
                }).catch(() => {

                })
            } else {
                setActiveTab(value);
            }
        }, [patientDetailsMode])

        const getPatientDetails = useCallback(() => {
            const payload = {};
            let patientDetails: any = {};
            setIsPatientDetailsLoading(true);
            setIsPatientDetailsLoaded(false);
            setIsPatientDetailsLoadingFailed(false);
            CommonService._patient.getPatientDetails(patientID, payload)
                .then((response: IAPIResponseType<any>) => {
                    if (response?.data) {
                        patientDetails = response.data;
                    }
                    setPatientDetails(patientDetails);
                    setIsPatientDetailsLoading(false);
                    setIsPatientDetailsLoaded(true);
                    setIsPatientDetailsLoadingFailed(false);
                })
                .catch((error: any) => {
                    setPatientDetails(patientDetails);
                    setIsPatientDetailsLoading(false);
                    setIsPatientDetailsLoaded(false);
                    setIsPatientDetailsLoadingFailed(true);
                });
        }, [patientID]);

        const handlePatientDetailsUpdate = useCallback((patientBasicDetails: any) => {
            setPatientDetails(() => {
                return {
                    ...patientBasicDetails
                }
            });
        }, []);

        const handleRemovePatient = useCallback((patientId: string | number) => {
            CommonService.openConfirmationDialog(
                {
                    confirmationTitle: 'Please confirm!',
                    confirmationSubTitle: 'Are you sure you want to delete the patient?',
                    disableEscKeyClose: false,
                    disableBackdropClose: true,
                    imagePath: `${ImageConfig.ConfirmationImage}`,
                    direction: "up",
                    yes: {
                        text: "Delete",
                        color: "primary"
                    },
                    no: {
                        text: "Cancel",
                        color: "primary"
                    }
                }).then(() => {
                CommonService._patient.removePatient(patientId).then((response) => {
                    CommonService._alert.showToast("Patient Successfully Deleted", "success");
                    navigate(CommonService._routeConfig.PatientListRoute())
                }).catch((error) => {
                    CommonService._alert.showToast(error?.error || "Error while removing patient", "error");
                })
            }).catch(() => {

            })
        }, [navigate])

        useEffect(() => {
            if (patientID) {
                getPatientDetails();
            }
        }, [patientID, getPatientDetails]);

        return (
            <div className="patient-details-screen details-screen screen">
                {
                    isPatientDetailsLoading && <div className="h-v-center">
                        <LottieFileGenarationComponent
                            loop={true}
                            autoplay={true}
                            animationData={ImageConfig.LoadingLottieFile}/>
                    </div>
                }
                {
                    isPatientDetailsLoadingFailed && <>
                        <StatusCardComponent title={"Patient details failed to load"}/>
                    </>
                }
                {
                    isPatientDetailsLoaded && <>
                        <div className={'details-module-title'}>Patient Details</div>
                        <div className="basic-details-wrapper">
                            <div className="details-screen-header-status-section">
                                <div className="details-screen-header">
                                    <HeadingComponent
                                        title={`${commonService.capitalizeFirstLetterOfEachWord(patientDetails?.firstName)} ${patientDetails?.middleName === null ? "" : commonService.capitalizeFirstLetterOfEachWord(patientDetails?.middleName)} ${commonService.capitalizeFirstLetterOfEachWord(patientDetails?.lastName)}`}
                                    />
                                    {canEdit && <ButtonComponent
                                        variant="contained"
                                        color={'primary'}
                                        handleClick={() => {
                                            patientID && handleRemovePatient(patientID)
                                        }}
                                    >
                                        Delete Patient
                                    </ButtonComponent>}
                                </div>
                            </div>
                            <div className="details-screen-sub-header">
                                <DetailsDataKeyValueComponent
                                    title={"Gender:"}
                                    isHorizontal={true}> {patientDetails.gender} </DetailsDataKeyValueComponent>
                                <DetailsDataKeyValueComponent
                                    title={"Patient ID:"}
                                    isHorizontal={true}> {patientDetails.id} </DetailsDataKeyValueComponent>
                                <DetailsDataKeyValueComponent
                                    title={"Created on:"}
                                    isHorizontal={true}>
                                    <>
                                        {commonService.formatDate(patientDetails?.createdAt)}
                                        &nbsp;|&nbsp;
                                        {commonService.formatTime(patientDetails?.createdAt)}
                                    </>
                                </DetailsDataKeyValueComponent>

                            </div>

                        </div>
                        <div className="clear-fix"/>
                        <div className="details-screen-body">
                            <TabsWrapperComponent>
                                <TabsComponent
                                    value={activeTab}
                                    allowScrollButtonsMobile={false}
                                    onUpdate={(e: any, v: any) => {
                                        handleActiveTabChange(v)
                                    }}
                                    variant={"fullWidth"}
                                >
                                    <TabComponent label="Patient Information" value={PATIENT_INFORMATION_TAB}/>
                                    <TabComponent label="Additional Details" value={ADDITIONAL_DETAILS_TAB}/>
                                    <TabComponent label="Insurance" value={INSURANCE_DETAILS_TAB}/>
                                    <TabComponent label="Order History" value={ORDER_HISTORY_TAB}/>
                                    {
                                        commonService.checkAccess('Patient', "patientActivityLog", currentUser?.userAccessModules) &&
                                        <TabComponent label="Activity Logs" value={PATIENT_ACTIVITY_LOGS}/>
                                    }
                                </TabsComponent>
                                <TabContentComponent selectedTab={activeTab} value={PATIENT_INFORMATION_TAB}>
                                    <PatientInformationDetailsComponent
                                        onUpdatePatientInformation={handlePatientDetailsUpdate}
                                        canEdit={canEdit}
                                        patientDetails={patientDetails}
                                        onModeChange={(mode: any) => {
                                            setPatientDetailsMode(mode);
                                        }
                                        }/>
                                </TabContentComponent>
                                <TabContentComponent selectedTab={activeTab} value={ADDITIONAL_DETAILS_TAB}>
                                    <PatientAdditionalDetailsComponent
                                        onUpdatePatientInformation={handlePatientDetailsUpdate}
                                        canEdit={canEdit}
                                        patientDetails={patientDetails}
                                        onModeChange={(mode: any) => {
                                            setPatientDetailsMode(mode);
                                        }}
                                    />
                                </TabContentComponent>
                                <TabContentComponent selectedTab={activeTab} value={INSURANCE_DETAILS_TAB}>
                                    <PatientInsuranceDetailsComponent
                                        patientID={patientID}
                                        canEdit={canEdit}
                                        onModeChange={(mode: any) => {
                                            setPatientDetailsMode(mode);
                                        }}
                                    />
                                </TabContentComponent>
                                <TabContentComponent selectedTab={activeTab} value={ORDER_HISTORY_TAB}>
                                    <PatientOrderHistoryComponent patientId={patientID}
                                    />
                                </TabContentComponent>
                                <TabContentComponent selectedTab={activeTab} value={PATIENT_ACTIVITY_LOGS}>
                                    <PatientActivityLogsComponent patientId={patientID}
                                    />
                                </TabContentComponent>
                            </TabsWrapperComponent>
                        </div>
                    </>
                }
            </div>
        );
    }
;
export default PatientDetailsScreen;
